import { Badge } from "@/components/ui/badge";
import {
	Tooltip,
	TooltipContent,
	TooltipProvider,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatShortDateTime } from "@/services/time";
import type { KnockMessage } from "@/types/zod/knock";
import { Check, Eye, MousePointerClick, Send } from "lucide-react";

interface Props {
	message: KnockMessage;
}

export function MessageBadge({ message }: Props) {
	if (message.clicked_at) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<Badge
							variant="secondary"
							className="flex items-center gap-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 transition-colors"
						>
							<MousePointerClick className="w-3 h-3" />
							Clicked
						</Badge>
					</TooltipTrigger>
					<TooltipContent>
						<div className="space-y-1">
							<p className="font-medium">Clicked</p>
							<p className="text-muted-foreground text-sm">
								{formatShortDateTime(message.clicked_at)}
							</p>
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}
	if (message.read_at) {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<Badge
							variant="secondary"
							className="flex items-center gap-1 bg-sky-100 hover:bg-sky-200 text-sky-700 transition-colors"
						>
							<Eye className="w-3 h-3" />
							Read
						</Badge>
					</TooltipTrigger>
					<TooltipContent>
						<div className="space-y-1">
							<p className="font-medium">Read</p>
							<p className="text-muted-foreground text-sm">
								at {formatShortDateTime(message.read_at)}
							</p>
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}
	if (message.status?.toLowerCase() === "delivered") {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<Badge
							variant="secondary"
							className="flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 transition-colors"
						>
							<Check className="w-3 h-3" />
							Delivered
						</Badge>
					</TooltipTrigger>
					<TooltipContent>
						<div className="space-y-1">
							<p className="font-medium">Delivered</p>
							<p className="text-muted-foreground text-sm">
								{formatShortDateTime(message.inserted_at)}
							</p>
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}
	if (message.status?.toLowerCase() === "sent") {
		return (
			<TooltipProvider>
				<Tooltip>
					<TooltipTrigger>
						<Badge
							variant="secondary"
							className="flex items-center gap-1 bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
						>
							<Send className="w-3 h-3" />
							Sent
						</Badge>
					</TooltipTrigger>
					<TooltipContent>
						<div className="space-y-1">
							<p className="font-medium">Sent</p>
							<p className="text-muted-foreground text-sm">
								{formatShortDateTime(message.inserted_at)}
							</p>
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		);
	}
	return (
		<TooltipProvider>
			<Tooltip>
				<TooltipTrigger>
					<Badge
						variant="outline"
						className="hover:bg-gray-100 text-muted-foreground transition-colors"
					>
						{message.status || "Pending"}
					</Badge>
				</TooltipTrigger>
				<TooltipContent>
					<div className="space-y-1">
						<p className="font-medium">{message.status || "Pending"}</p>
						<p className="text-muted-foreground text-sm">
							{formatShortDateTime(message.inserted_at)}
						</p>
					</div>
				</TooltipContent>
			</Tooltip>
		</TooltipProvider>
	);
}
